// Import necessary React hooks
import { useState } from "react";
import { useNavigate } from "react-router-dom";
// Import axios utilities
import axios from "axios";
// Import necessary Material-UI components
import { Grid } from "@mui/material";
// Import custom validation, functions, hooks and components
import {
  useAuthInfo,
  ApprovalDetails,
  authErrorRedirect,
  apiURI,
} from "../../common";
import { ApprovalButtonsDataGrid } from "./";
import { validateApprovalInput } from "../../requests/validation/validateApprovalInput";

/**
 * ItemApprovals component for managing approval process of an item
 * @param {Object} props - Component props including modal state, data, and callbacks.
 */
export function ItemApprovals({
  itemApprovals,
  supplier,
  supplierValues,
  sampleRequestID,
  sampleItemID,
  setReloadData,
}) {
  // State for managing comments input
  const [value, setValue] = useState({ comments: "" });
  // State for managing errors
  const [errors, setErrors] = useState();
  // Retrieve decoded authentication information
  const decoded = useAuthInfo();
  // Navigation hook from React Router
  const history = useNavigate();
  // State for controlling form submission loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handler function for changes in comments input
  const handleChange = (event) => {
    setValue({ comments: event.target.value });
  };

  function saveApproval(addSupplier) {
    setIsSubmitting(true);
    axios
      .post(`${apiURI}/form/approveSupplier`, addSupplier)
      .then(() => {
        // Reset loading state
        setIsSubmitting(false);
        // Reload data after successful approval
        setReloadData(true);
      })
      .catch((response) => {
        console.log("error in axios login call react");
        // Clear errors
        setErrors({});
        // Set errors received from server response
        setErrors(response.response.data);
        // Redirect on authentication error
        authErrorRedirect(response.response.data, history);
      });
  }
  // Function to handle approval actions
  const handleApproval = ({ e, approval, value }) => {
    e.preventDefault();
    // Clear any previous errors
    setErrors();

    // Append standard comments for Head of Department (HOD) user
    let commentValue = value;
    if (decoded.role === "hod") {
      if (approval === true) {
        commentValue = { comments: "Approved" };
      }
      if (approval === false) {
        commentValue = { comments: "Rejected" };
      }
    }
    setValue(commentValue);

    //Validate comments input
    const { validationErrors, isValid } = validateApprovalInput(commentValue);
    !isValid && setErrors(validationErrors);

    //Create object for API call
    const addSupplier = {
      approval,
      role: decoded.role,
      email: decoded.email,
      comments: value.comments,
      sampleRequestID: sampleRequestID,
      sampleItemID: sampleItemID,
      supplierValues: supplierValues,
      qualityReportsID: supplier.qualityReportsID,
    };

    // API call if validation passes
    isValid && saveApproval(addSupplier);

    // eslint-disable-next-line no-console
  };
  // Log errors to console if any
  errors && console.log("error", errors);

  return (
    <Grid
      container
      style={{
        fontSize: "14px",
        marginTop: "25px",
        marginBottom: "25px",
      }}
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      {/* Render Chef Approval section if user approval exists */}
      {itemApprovals.userApproval && (
        <>
          <Grid item xs={12} sm={12}>
            <span>
              <b>Chef Approval:</b>
            </span>
            <ApprovalDetails data={itemApprovals.userApproval} />
          </Grid>
          <Grid item xs={8}>
            {/* Render approval buttons for Chef if conditions are met */}
            {supplier.erpName.toLowerCase() !== "pending" &&
              itemApprovals.userApproval.status.toLowerCase() === "pending" &&
              decoded.role === "chef" && (
                <ApprovalButtonsDataGrid
                  value={value}
                  errors={errors}
                  handleChange={handleChange}
                  handleApproval={handleApproval}
                  id={`${supplier.qualityReportsID}-exec-chef`}
                  isSubmitting={isSubmitting}
                />
              )}
          </Grid>{" "}
        </>
      )}
      {/* Render Exec Chef Approval section */}
      {supplier.receiptDate.toLowerCase() !== "pending" &&
        (itemApprovals.userApproval
          ? itemApprovals.userApproval.status.toLowerCase() === "accepted"
          : true) && (
          <Grid item xs={12} sm={12} mt={2}>
            <span>
              <b>Exec Chef Approval:</b>
            </span>
            <ApprovalDetails data={itemApprovals.execChefApproval} />
          </Grid>
        )}
      <Grid item xs={8}>
        {/* Render approval buttons for Executive Chef if conditions are met */}
        {supplier.receiptDate.toLowerCase() !== "pending" &&
          (itemApprovals.userApproval
            ? itemApprovals.userApproval.status.toLowerCase() === "accepted"
            : true) &&
          itemApprovals.execChefApproval.status.toLowerCase() === "pending" &&
          decoded.role === "executiveChef" && (
            <ApprovalButtonsDataGrid
              value={value}
              errors={errors}
              handleChange={handleChange}
              handleApproval={handleApproval}
              id={`${supplier.qualityReportsID}-exec-chef`}
              isSubmitting={isSubmitting}
            />
          )}
      </Grid>
      {/* Render HOD Approval section */}
      {supplier.receiptDate.toLowerCase() !== "pending" &&
        itemApprovals.execChefApproval.status.toLowerCase() === "accepted" && (
          <Grid item xs={12} sm={12} mt={2}>
            <span>
              <b>HOD Approval:</b>
            </span>
            <ApprovalDetails data={itemApprovals.HODApproval} />
          </Grid>
        )}
      <Grid item xs={8}>
        {/* Render approval buttons for HOD if conditions are met */}
        {supplier.receiptDate.toLowerCase() !== "pending" &&
          itemApprovals.execChefApproval.status.toLowerCase() === "accepted" &&
          itemApprovals.HODApproval.status.toLowerCase() === "pending" &&
          decoded.role === "hod" && (
            <ApprovalButtonsDataGrid
              value={value}
              errors={errors}
              handleChange={handleChange}
              handleApproval={handleApproval}
              id={`${supplier.qualityReportsID}-hod`}
              approvalHod={true}
              isSubmitting={isSubmitting}
            />
          )}
      </Grid>
    </Grid>
  );
}
